import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import { Redo, Save, Undo } from "@mui/icons-material";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import dayjs from "dayjs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../../../hooks/AuthProvider";
import {
  FORMS_FORM_BUILDER,
  FORMS_FORM_BUILDER_GENERATE,
  FORMS_FORM_IMAGES,
} from "../../../config/urls";
import {
  ColorsConstants,
  borderColors,
  grayFontColor,
} from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import BackButton from "../../../common/back-button";
import ButtonOne from "../../../common/normal-button";
import ButtonTwo from "../../../common/dashboard-button";
import StepTabs from "../../../common/step-tabs";
import {
  clearUnsavedChanges,
  useUnsavedChanges,
} from "../../../common/unsaved-changes";
import FormPreview from "../Components/FormPreview";
import JsonView from "./JsonView";
import TestSendDialog from "./TestSendDialog";
import FormDataDestinations from "../FormDataDestinations";
import IssuesPanel from "./IssuesPanel";
import PhonePreview from "./PhonePreview";
import PublishDialog from "./PublishDialog";
import ScreenCard from "./ScreenCard";
import {
  copyComponent,
  fieldKeyOwners,
  mergeServerModel,
  newComponent,
  findBranchIndex,
  newScreen,
  ruleFieldsFor,
} from "./builderConstants";

const HISTORY_LIMIT = 100;
// Edits closer together than this undo as one step (e.g. typing a sentence).
const HISTORY_COALESCE_MS = 1000;
const AUTOSAVE_MS = 10000;
const GENERATE_DEBOUNCE_MS = 500;

const STEPS = [
  { label: "Details" },
  { label: "Build screens" },
  { label: "Data & destinations" },
];

const FormBuilder = ({ pageParams, onNavigate }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();
  // AuthProvider recreates getValidToken on every render; keep callbacks stable.
  const tokenRef = useRef(getValidToken);
  tokenRef.current = getValidToken;
  const trackChanges = useUnsavedChanges();

  const formId = pageParams?.id;
  const source = pageParams?.source || "view_forms";

  // 0 = Build screens, 1 = Data & destinations
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [legacy, setLegacy] = useState(null);

  const [model, setModel] = useState(null);
  const [limits, setLimits] = useState(null);
  const [lockedKeys, setLockedKeys] = useState({});
  const [editable, setEditable] = useState(false);
  const [issues, setIssues] = useState([]);
  const [flowJson, setFlowJson] = useState(null);

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [savedTick, setSavedTick] = useState(0);
  const [conflict, setConflict] = useState("");
  const [alert, setAlert] = useState({ message: "", severity: "error" });

  const [openScreen, setOpenScreen] = useState(0);
  const [openItem, setOpenItem] = useState(null);
  const [previewScreen, setPreviewScreen] = useState(0);
  const [view, setView] = useState("preview");
  const [interactive, setInteractive] = useState(true);
  const [copied, setCopied] = useState(false);

  const [dataControls, setDataControls] = useState({ dirty: false, saving: false });
  const [publishOpen, setPublishOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [historySize, setHistorySize] = useState({ undo: 0, redo: 0 });

  const modelRef = useRef(null);
  const updatedAtRef = useRef(null);
  const editVersionRef = useRef(0);
  const savePromiseRef = useRef(null);
  const generateCounterRef = useRef(0);
  const historyRef = useRef({ past: [], future: [], lastPush: 0 });
  modelRef.current = model;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const getHeaders = useCallback(
    async (json = true) => {
      const token = await tokenRef.current();
      return {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
        ...(json ? { "Content-Type": "application/json" } : {}),
      };
    },
    [tenant],
  );

  const readError = async (response, fallback) => {
    const data = await response.json().catch(() => ({}));
    return data?.error || data?.detail || fallback;
  };

  // ---- Load -------------------------------------------------------------
  const load = useCallback(async () => {
    if (!formId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const params = pageParams?.presetId ? `?preset_id=${pageParams.presetId}` : "";
      const response = await fetch(`${FORMS_FORM_BUILDER(formId)}${params}`, {
        headers: await getHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, "Failed to load the form"));
      const data = await response.json();

      if (data.legacy) {
        setLegacy({ flowJson: data.flow_json, problems: data.problems });
        return;
      }
      setLegacy(null);
      setModel(data.builder_model);
      setLimits(data.limits);
      setLockedKeys(data.locked_keys || {});
      setEditable(data.editable);
      setIssues(data.issues || []);
      updatedAtRef.current = data.form?.updated_at || null;
      editVersionRef.current = 0;
      historyRef.current = { past: [], future: [], lastPush: 0 };
      setHistorySize({ undo: 0, redo: 0 });
      setDirty(false);
      setConflict("");
      setOpenScreen(0);
      setOpenItem(null);
      setPreviewScreen(0);
      clearUnsavedChanges();
    } catch (error) {
      setLoadError(error.message || "Failed to load the form");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, getHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  // The unsaved-changes guard covers the sidebar and closing the tab. It follows the
  // `dirty` flag, not the model: a save rewrites the model (server-made keys), which
  // would otherwise look like a new change against the model as first loaded.
  const loaded = model !== null;
  useEffect(() => {
    if (loaded) trackChanges({ dirty });
  }, [loaded, dirty, trackChanges]);

  // ---- Live validation and Flow JSON -------------------------------------
  useEffect(() => {
    if (!model || !formId) return undefined;
    const run = ++generateCounterRef.current;
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(FORMS_FORM_BUILDER_GENERATE(formId), {
          method: "POST",
          headers: await getHeaders(),
          body: JSON.stringify({ builder_model: model }),
        });
        if (!response.ok || run !== generateCounterRef.current) return;
        const data = await response.json();
        setFlowJson(data.flow_json);
        setIssues(data.issues || []);
      } catch (error) {
        // The last known issues stay; saving reports problems too.
      }
    }, GENERATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [model, formId, getHeaders]);

  // ---- Save --------------------------------------------------------------
  const save = useCallback(
    ({ autosave = false } = {}) => {
      const run = async () => {
        if (savePromiseRef.current) await savePromiseRef.current.catch(() => null);
        if (!editable || !modelRef.current) return true;
        if (autosave && editVersionRef.current === 0) return true;

        const sentVersion = editVersionRef.current;
        setSaving(true);
        try {
          const response = await fetch(
            `${FORMS_FORM_BUILDER(formId)}${autosave ? "?autosave=1" : ""}`,
            {
              method: "PUT",
              headers: await getHeaders(),
              body: JSON.stringify({
                builder_model: modelRef.current,
                expected_updated_at: updatedAtRef.current,
              }),
            },
          );
          const data = await response.json().catch(() => ({}));
          if (response.status === 409) {
            setConflict(data?.error || "Someone else edited this form.");
            return false;
          }
          if (!response.ok) throw new Error(data?.error || "Couldn't save the form");

          updatedAtRef.current = data.updated_at;
          setIssues(data.issues || []);
          setModel((current) => mergeServerModel(current, data.builder_model));
          // Undo shouldn't bring back fields without the keys the server just made.
          const history = historyRef.current;
          const fill = (m) => mergeServerModel(m, data.builder_model, { fillScreenIds: false });
          history.past = history.past.map(fill);
          history.future = history.future.map(fill);
          const stillDirty = editVersionRef.current !== sentVersion;
          setDirty(stillDirty);
          if (!stillDirty) clearUnsavedChanges();
          setSavedAt(new Date());
          setAlert((current) => (current.severity === "error" && current.message ? { message: "", severity: "error" } : current));
          return true;
        } catch (error) {
          setAlert({ message: error.message || "Couldn't save the form", severity: "error" });
          return false;
        } finally {
          setSaving(false);
          setSavedTick((tick) => tick + 1);
        }
      };

      const promise = run();
      savePromiseRef.current = promise;
      promise.finally(() => {
        if (savePromiseRef.current === promise) savePromiseRef.current = null;
      });
      return promise;
    },
    [editable, formId, getHeaders],
  );

  // Autosave: once the form has unsaved changes, save 10 s later.
  useEffect(() => {
    if (!dirty || !editable || conflict || step !== 0) return undefined;
    const timer = setTimeout(() => save({ autosave: true }), AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [dirty, editable, conflict, step, savedTick, save]);

  // ---- Editing -----------------------------------------------------------
  const syncHistorySize = () =>
    setHistorySize({
      undo: historyRef.current.past.length,
      redo: historyRef.current.future.length,
    });

  const change = (updater) => {
    const history = historyRef.current;
    const now = Date.now();
    if (history.past.length === 0 || now - history.lastPush > HISTORY_COALESCE_MS) {
      history.past.push(modelRef.current);
      if (history.past.length > HISTORY_LIMIT) history.past.shift();
    }
    history.lastPush = now;
    history.future = [];
    syncHistorySize();

    setModel((current) => updater(current));
    editVersionRef.current += 1;
    setDirty(true);
  };

  // Shows a model from the undo/redo history.
  const restoreModel = (next) => {
    modelRef.current = next;
    setModel(next);
    editVersionRef.current += 1;
    setDirty(true);
    setOpenScreen((current) => (current === null ? null : Math.min(current, next.screens.length - 1)));
    setPreviewScreen((current) => Math.min(current, next.screens.length - 1));
    setOpenItem(null);
    syncHistorySize();
  };

  const undo = () => {
    const history = historyRef.current;
    if (!history.past.length || !editable) return;
    history.future.push(modelRef.current);
    history.lastPush = 0;
    restoreModel(history.past.pop());
  };

  const redo = () => {
    const history = historyRef.current;
    if (!history.future.length || !editable) return;
    history.past.push(modelRef.current);
    history.lastPush = 0;
    restoreModel(history.future.pop());
  };

  // Ctrl/Cmd+Z and Ctrl/Cmd+Shift+Z, except while typing (fields have their own undo).
  const shortcutRef = useRef(null);
  shortcutRef.current = { undo, redo, active: step === 0 };
  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "z") return;
      const { tagName, isContentEditable } = event.target;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tagName) || isContentEditable) return;
      if (!shortcutRef.current.active) return;
      event.preventDefault();
      if (event.shiftKey) shortcutRef.current.redo();
      else shortcutRef.current.undo();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const reorderScreens = (activeId, overId, ids) => {
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0 || from === to) return;
    change((m) => ({ ...m, screens: arrayMove(m.screens, from, to) }));
    setOpenScreen(to);
    setPreviewScreen(to);
    setOpenItem(null);
  };

  const reorderComponents = (si, activeId, overId) =>
    change((m) => ({
      ...m,
      screens: m.screens.map((s, i) => {
        if (i !== si) return s;
        const from = s.components.findIndex((c) => c.id === activeId);
        const to = s.components.findIndex((c) => c.id === overId);
        return from < 0 || to < 0 ? s : { ...s, components: arrayMove(s.components, from, to) };
      }),
    }));

  const updateScreen = (si, patch) =>
    change((m) => ({
      ...m,
      screens: m.screens.map((s, i) => (i === si ? { ...s, ...patch } : s)),
    }));

  const updateComponent = (si, ci, patch) =>
    change((m) => ({
      ...m,
      screens: m.screens.map((s, i) =>
        i === si
          ? { ...s, components: s.components.map((c, j) => (j === ci ? { ...c, ...patch } : c)) }
          : s,
      ),
    }));

  const addComponent = (si, type) => {
    const component = newComponent(type);
    change((m) => ({
      ...m,
      screens: m.screens.map((s, i) =>
        i === si ? { ...s, components: [...s.components, component] } : s,
      ),
    }));
    setOpenItem(`${si}-${component.id}`);
    setPreviewScreen(si);
  };

  // Inserts a copy right below the original and opens it.
  const duplicateComponent = (si, ci) => {
    const component = copyComponent(model.screens[si].components[ci]);
    change((m) => ({
      ...m,
      screens: m.screens.map((s, i) =>
        i === si
          ? { ...s, components: [...s.components.slice(0, ci + 1), component, ...s.components.slice(ci + 1)] }
          : s,
      ),
    }));
    setOpenItem(`${si}-${component.id}`);
    setPreviewScreen(si);
  };

  const deleteComponent = (si, ci) => {
    change((m) => ({
      ...m,
      screens: m.screens.map((s, i) =>
        i === si ? { ...s, components: s.components.filter((_, j) => j !== ci) } : s,
      ),
    }));
    setOpenItem(null);
  };

  const addScreen = () => {
    const next = model.screens.length;
    change((m) => ({ ...m, screens: [...m.screens, newScreen()] }));
    setOpenScreen(next);
    setPreviewScreen(next);
    setOpenItem(null);
  };

  const deleteScreen = (si) => {
    const screen = model.screens[si];
    if (
      screen.components.length > 1 &&
      !window.confirm(`Delete screen ${si + 1} and its ${screen.components.length} components?`)
    ) {
      return;
    }
    change((m) => ({ ...m, screens: m.screens.filter((_, i) => i !== si) }));
    setOpenScreen(0);
    setPreviewScreen(0);
    setOpenItem(null);
  };

  const uploadImage = async (file) => {
    const body = new FormData();
    body.append("file", file);
    const response = await fetch(FORMS_FORM_IMAGES(formId), {
      method: "POST",
      headers: await getHeaders(false),
      body,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.error || "Upload failed");
    return data.image_id;
  };

  const selectIssue = (issue) => {
    if (issue.screen_index === null || issue.screen_index === undefined) return;
    setOpenScreen(issue.screen_index);
    setPreviewScreen(issue.screen_index);
    if (issue.component_id) {
      setOpenItem(`${issue.screen_index}-${issue.component_id}`);
      setTimeout(
        () =>
          document
            .getElementById(`component-${issue.component_id}`)
            ?.scrollIntoView({ block: "center", behavior: "smooth" }),
        150,
      );
    }
  };

  // ---- Navigation --------------------------------------------------------
  const goToViewForms = () => onNavigate("whatsapp_forms", { tab: 1 });

  const finalFlowJson = async () => {
    try {
      const response = await fetch(FORMS_FORM_BUILDER_GENERATE(formId), {
        method: "POST",
        headers: await getHeaders(),
        body: JSON.stringify({ builder_model: modelRef.current }),
      });
      if (response.ok) return (await response.json()).flow_json;
    } catch (error) {
      // Falls back to the last generated JSON.
    }
    return flowJson;
  };

  const goToDetails = async () => {
    if (!(await save())) return;
    if (source !== "create_form") {
      goToViewForms();
      return;
    }
    onNavigate("whatsapp_forms", {
      tab: 0,
      id: formId,
      name: pageParams?.name,
      categories: pageParams?.categories,
      presetId: pageParams?.presetId,
      form_json: await finalFlowJson(),
    });
  };

  const handleBack = () => (step === 1 ? setStep(0) : goToDetails());

  const handleNext = async () => {
    if (await save()) setStep(1);
  };

  const handleSaveDraft = async () => {
    if (step === 0) {
      if (await save()) goToViewForms();
      return;
    }
    if (dataControls.dirty) await dataControls.save?.();
    goToViewForms();
  };

  const handleSendTest = async () => {
    // The test uses the saved version, so save first.
    if (editable && step === 0 && !(await save())) return;
    setTestOpen(true);
  };

  const handlePublish = async () => {
    if (dataControls.dirty) await dataControls.save?.();
    setPublishOpen(true);
  };

  const handleStepClick = (index) => {
    if (index === 0) {
      if (source === "create_form") goToDetails();
      return;
    }
    if (index === 1) setStep(0);
    else handleNext();
  };

  const steps = useMemo(
    () =>
      STEPS.map((s, i) => ({
        ...s,
        disabled: i === 0 && source !== "create_form",
      })),
    [source],
  );

  // ---- Render ------------------------------------------------------------
  if (!formId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Open a form from View Forms to edit it.</Alert>
        <Box sx={{ mt: 2 }}>
          <ButtonTwo title="Back to View Forms" onClick={goToViewForms} />
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // A form the builder can't import (made outside it): shown read-only. It still
  // works for sends, template buttons and Submissions.
  if (legacy) {
    return (
      <Stack sx={{ p: 3 }} spacing={2}>
        <Box>
          <BackButton onClick={goToViewForms} />
        </Box>
        <Alert severity="info" sx={{ "& .MuiAlert-message": typography.bodyLarge }}>
          This form was made outside the form builder, so it can't be edited here. It still
          works for sending, template buttons and submissions. To change it, create a new form.
          {legacy.problems?.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, ...typography.bodySmall }}>
              {legacy.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </Box>
          )}
        </Alert>
        {legacy.flowJson && <FormPreview formJson={legacy.flowJson} />}
      </Stack>
    );
  }

  if (loadError || !model) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ "& .MuiAlert-message": typography.bodyLarge }}>
          {loadError || "Couldn't load the form"}
        </Alert>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <ButtonTwo title="Back to View Forms" onClick={goToViewForms} />
          <ButtonOne title="Try again" onClick={load} />
        </Stack>
      </Box>
    );
  }

  const statusText = conflict
    ? ""
    : saving
      ? "Saving…"
      : dirty
        ? "Unsaved changes"
        : savedAt
          ? `Saved ${dayjs(savedAt).format("HH:mm")}`
          : "";

  const screenIndex = Math.min(previewScreen, model.screens.length - 1);
  const keyOwners = fieldKeyOwners(model);

  return (
    <Stack sx={{ px: 2, pb: 3 }} spacing={2}>
      <Box>
        <BackButton onClick={handleBack} />
      </Box>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}
      >
        <Stack>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            Message Templates › WhatsApp Forms ›{" "}
            {source === "create_form" ? "Create Form" : "Edit Form"}
          </Typography>
          <Typography sx={typography.h5}>
            {source === "create_form" ? "Create WhatsApp Form" : "Edit WhatsApp Form"}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {statusText && (
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mr: 1 }}>
              {statusText}
            </Typography>
          )}
          <ButtonTwo title="Send test" onClick={handleSendTest} disabled={saving} />
          <ButtonTwo
            title="Save as Draft"
            onClick={handleSaveDraft}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            disabled={saving || dataControls.saving}
          />
          {step === 0 ? (
            <ButtonOne title="Next" onClick={handleNext} disabled={saving} />
          ) : (
            <ButtonOne title="Publish to Meta" onClick={handlePublish} disabled={dataControls.saving} />
          )}
        </Stack>
      </Stack>

      <StepTabs steps={steps} current={step + 1} onStepClick={handleStepClick} />

      {alert.message && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ message: "", severity: "error" })}
          sx={{ "& .MuiAlert-message": typography.bodyLarge }}
        >
          {alert.message}
        </Alert>
      )}

      {conflict && (
        <Alert
          severity="warning"
          action={<ButtonTwo title="Reload" onClick={load} />}
          sx={{ "& .MuiAlert-message": typography.bodyLarge }}
        >
          {conflict}
        </Alert>
      )}

      {!editable && (
        <Alert severity="info" sx={{ "& .MuiAlert-message": typography.bodyLarge }}>
          This form can't be edited. Only drafts can be changed, by an Admin or Manager.
        </Alert>
      )}

      {step === 1 && (
        <FormDataDestinations formId={formId} onControlsChange={setDataControls} />
      )}

      {step === 0 && (
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 400px" } }}>
            {/* Editor */}
            <Box
              component="fieldset"
              disabled={!editable || Boolean(conflict)}
              sx={{
                border: 0,
                m: 0,
                p: 2,
                minWidth: 0,
                borderRight: { lg: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                <Box>
                  <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>Edit content</Typography>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                    Add screens and content. Changes show in the preview.
                  </Typography>
                </Box>
                <Stack direction="row">
                  <Tooltip title="Undo (Ctrl+Z)">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Undo"
                        disabled={historySize.undo === 0}
                        onClick={undo}
                      >
                        <Undo fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Redo (Ctrl+Shift+Z)">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Redo"
                        disabled={historySize.redo === 0}
                        onClick={redo}
                      >
                        <Redo fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={({ active, over }) =>
                  over &&
                  reorderScreens(
                    active.id,
                    over.id,
                    model.screens.map((s, i) => s.id || `new-${i}`),
                  )
                }
              >
              <SortableContext
                items={model.screens.map((s, i) => s.id || `new-${i}`)}
                strategy={verticalListSortingStrategy}
              >
              {model.screens.map((screen, si) => (
                <ScreenCard
                  key={screen.id || `new-${si}`}
                  sortableId={screen.id || `new-${si}`}
                  screen={screen}
                  index={si}
                  total={model.screens.length}
                  open={openScreen === si}
                  openItem={openItem}
                  limits={limits}
                  formId={formId}
                  lockedKeys={lockedKeys}
                  keyOwners={keyOwners}
                  ruleFields={ruleFieldsFor(model, si)}
                  laterScreens={model.screens
                    .slice(si + 1)
                    .map((s, i) => ({ id: s.id, title: s.title, index: si + 1 + i }))}
                  isBranchTarget={findBranchIndex(model) >= 0 && si > findBranchIndex(model)}
                  uploadImage={uploadImage}
                  onToggle={() => {
                    setOpenScreen(openScreen === si ? null : si);
                    setOpenItem(null);
                    setPreviewScreen(si);
                  }}
                  onToggleItem={(id) => setOpenItem(openItem === id ? null : id)}
                  onScreenChange={(patch) => updateScreen(si, patch)}
                  onComponentChange={(ci, patch) => updateComponent(si, ci, patch)}
                  onAddComponent={(type) => addComponent(si, type)}
                  onDeleteComponent={(ci) => deleteComponent(si, ci)}
                  onDuplicateComponent={(ci) => duplicateComponent(si, ci)}
                  onDeleteScreen={() => deleteScreen(si)}
                  onReorderComponents={(activeId, overId) =>
                    reorderComponents(si, activeId, overId)
                  }
                />
              ))}
              </SortableContext>
              </DndContext>

              <Box
                component="button"
                type="button"
                disabled={model.screens.length >= (limits?.max_screens || 8)}
                onClick={addScreen}
                sx={{
                  ...typography.bodySmall,
                  fontWeight: 500,
                  px: 1.75,
                  py: 1,
                  borderRadius: "6px",
                  backgroundColor: "#FFFFFF",
                  border: `1px solid ${ColorsConstants.primaryBackgroundColor}`,
                  color: ColorsConstants.primaryBackgroundColor,
                  cursor: "pointer",
                  "&:disabled": { opacity: 0.5, cursor: "default" },
                }}
              >
                + Add new screen
              </Box>
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.75 }}>
                Up to {limits?.max_screens || 8} screens · {limits?.max_components_per_screen || 50}{" "}
                components per screen
              </Typography>

              <IssuesPanel issues={issues} onSelect={selectIssue} />
            </Box>

            {/* Preview */}
            <Box sx={{ p: 2, backgroundColor: "#F8F9FB", minWidth: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
                <Box
                  sx={{
                    display: "flex",
                    border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                    borderRadius: "6px",
                    overflow: "hidden",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  {[
                    { value: "preview", label: "Preview" },
                    { value: "json", label: "JSON" },
                  ].map((tab) => (
                    <Box
                      key={tab.value}
                      component="button"
                      type="button"
                      onClick={() => setView(tab.value)}
                      sx={{
                        ...typography.bodySmall,
                        border: 0,
                        px: 1.75,
                        py: 0.75,
                        cursor: "pointer",
                        backgroundColor: view === tab.value ? ColorsConstants.primaryBackgroundColor : "transparent",
                        color: view === tab.value ? "#fff" : grayFontColor,
                      }}
                    >
                      {tab.label}
                    </Box>
                  ))}
                </Box>
                {view === "preview" && (
                  <Stack direction="row" alignItems="center">
                    <Switch
                      size="small"
                      checked={interactive}
                      onChange={(e) => setInteractive(e.target.checked)}
                      inputProps={{ "aria-label": "Interactive" }}
                    />
                    <Typography sx={typography.bodySmall}>Interactive</Typography>
                  </Stack>
                )}
              </Stack>

              {view === "preview" ? (
                <>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={screenIndex}
                    onChange={(e) => setPreviewScreen(Number(e.target.value))}
                    InputProps={{ sx: typography.bodySmall }}
                    sx={{ mb: 1.5, backgroundColor: "#fff" }}
                  >
                    {model.screens.map((screen, i) => (
                      <MenuItem key={screen.id || i} value={i} sx={typography.bodySmall}>
                        {`Screen ${i + 1}: ${screen.title}`}
                      </MenuItem>
                    ))}
                  </TextField>
                  <PhonePreview
                    model={model}
                    screenIndex={screenIndex}
                    interactive={interactive}
                    formId={formId}
                    onScreenChange={setPreviewScreen}
                  />
                </>
              ) : (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                      Flow JSON · generated
                    </Typography>
                    <ButtonTwo
                      title={copied ? "Copied" : "Copy"}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(JSON.stringify(flowJson, null, 2));
                          setCopied(true);
                          setTimeout(() => setCopied(false), 1200);
                        } catch (error) {
                          setAlert({ message: "Couldn't copy to the clipboard", severity: "error" });
                        }
                      }}
                    />
                  </Stack>
                  <JsonView value={flowJson} />
                </>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      <TestSendDialog
        open={testOpen}
        formId={formId}
        published={!editable}
        onClose={() => setTestOpen(false)}
      />

      <PublishDialog
        open={publishOpen}
        formId={formId}
        onClose={() => setPublishOpen(false)}
        onPublished={() => {
          setPublishOpen(false);
          goToViewForms();
        }}
      />
    </Stack>
  );
};

export default FormBuilder;
